import type { State } from "@/features/core/store";
import type { EllipsoidPath, LocalizingSetPath } from "@/features/solver/types";
import type { ViewportRenderSnapshot } from "@/features/viewport/types";
import { ellipsoidStride, localizingSetStride } from "@lpviz/solver-engine/localization";
import { Group, Matrix4 } from "three";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import type { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { FatLine, lineDepthMaterial, lineGeometry } from "../helpers/fatLine";
import { iterateHeightOrFloor } from "../helpers/iteratePositions";
import { placeInSlot } from "../Layer";
import { PALETTE, STROKE } from "../palette";
import { ZScaledLayer } from "./base/LayerBase";

// the localizing polyhedron is the region actually still under consideration
const POLYGON_OPACITY = 0.7;
const ACTIVE_OPACITY = 0.95;
// Only a mild fade with age: each ellipsoid is ~77% the area of the one before, so the older ones
// are also the *larger* ones, the ones that frame the whole picture.
const OLDEST_OPACITY = 0.45;
// How many of the run's ellipsoids are drawn at once, sampled evenly across everything computed so
// far rather than taken from the tail, so the whole shrinking sequence shows (including the initial
// ellipsoid framing the region) instead of a cluster of near-identical ellipses at the optimum.
const TRAIL_COUNT = 10;
const CIRCLE_SEGMENTS = 96;

// Unit circle as segment pairs, shared by every slot: each ellipse is this circle under the linear
// map L with L Lᵀ = P (a Cholesky factor; any square root works, the circle being rotation
// invariant), so a rebuild only writes 10 matrices instead of 10 geometries.
function buildUnitCirclePositions(): Float32Array {
  const positions = new Float32Array(CIRCLE_SEGMENTS * 6);
  for (let i = 0; i < CIRCLE_SEGMENTS; i++) {
    const a = (i / CIRCLE_SEGMENTS) * Math.PI * 2;
    const bAngle = ((i + 1) / CIRCLE_SEGMENTS) * Math.PI * 2;
    const base = i * 6;
    positions[base] = Math.cos(a);
    positions[base + 1] = Math.sin(a);
    positions[base + 2] = 0;
    positions[base + 3] = Math.cos(bAngle);
    positions[base + 4] = Math.sin(bAngle);
    positions[base + 5] = 0;
  }
  return positions;
}

// Evenly spaced indices in [0, active], newest last, without duplicates.
function sampleIndices(active: number, out: number[]): void {
  out.length = 0;
  if (active <= 0) {
    out.push(0);
    return;
  }
  const wanted = Math.min(TRAIL_COUNT, active + 1);
  for (let j = 0; j < wanted; j++) {
    const index = Math.round((active * j) / (wanted - 1));
    if (out[out.length - 1] !== index) out.push(index);
  }
}

// The lower-triangular Cholesky factor of the 2x2 shape matrix, written into `matrix` with the
// center and the matching iterate's height `z` (so in 3D each ellipse sits at its own iterate).
// Returns false when P is not numerically positive definite: the ellipse is then skipped rather
// than drawn with a NaN transform. A 3-variable ellipsoid (stride 9) needs its own geometry and is
// not drawn yet.
function writeEllipseMatrix(matrix: Matrix4, ellipsoids: EllipsoidPath, index: number, z: number): boolean {
  if (ellipsoids.stride !== ellipsoidStride(2)) return false;
  const base = index * ellipsoids.stride;
  const cx = ellipsoids.data[base]!;
  const cy = ellipsoids.data[base + 1]!;
  const p11 = ellipsoids.data[base + 2]!;
  const p12 = ellipsoids.data[base + 3]!;
  const p22 = ellipsoids.data[base + 4]!;

  if (!(p11 > 0)) return false;
  const l11 = Math.sqrt(p11);
  const l21 = p12 / l11;
  const inner = p22 - l21 * l21;
  if (!(inner > 0)) return false;
  const l22 = Math.sqrt(inner);
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return false;

  // prettier-ignore
  matrix.set(
    l11, 0,   0, cx,
    l21, l22, 0, cy,
    0,   0,   1, z,
    0,   0,   0, 1,
  );
  return true;
}

// The ellipsoid method's shrinking ellipsoids, drawn under the iterate path and following the
// active iterate (the last one solved, or the one hovered in the log / replayed). Raw z is baked
// into each ellipse's transform; zScale and the 2D/3D transition flatten ride on scale.z, exactly
// as for the iterate path.
export class EllipsoidLayer extends ZScaledLayer {
  readonly object3D = new Group();
  readonly slot = "ellipsoid" as const;
  override readonly invalidationKeys = ["iterate"] as const;
  private readonly geometry: LineSegmentsGeometry;
  private readonly slots: LineSegments2[] = [];
  private readonly polygon = this.own(new FatLine({ color: PALETTE.ellipsoid, linewidth: STROKE.localizingPolygon, opacity: POLYGON_OPACITY, slot: this.slot }));
  private readonly matrix = new Matrix4();
  private readonly indices: number[] = [];
  private polygonScratch = new Float32Array(0);

  constructor() {
    super();
    const geometry = lineGeometry();
    geometry.setPositions(buildUnitCirclePositions());
    this.geometry = geometry;
    this.object3D.add(this.polygon.object3D);

    for (let slot = 0; slot < TRAIL_COUNT; slot++) {
      const segments = placeInSlot(new LineSegments2(geometry, slotMaterial(slot, false)), this.slot);
      segments.matrixAutoUpdate = false;
      this.slots.push(segments);
      this.object3D.add(segments);
    }
  }

  protected dependencies(state: State, snap: ViewportRenderSnapshot): readonly unknown[] {
    return [state.iterateEllipsoids, state.iterateLocalizingSets, state.iteratePath, state.highlightIteratePathIndex, snap.mode];
  }

  protected rebuild(state: State, snap: ViewportRenderSnapshot): void {
    const ellipsoids = state.iterateEllipsoids;
    if (!ellipsoids || ellipsoids.count === 0) {
      this.hideFrom(0);
      return;
    }

    // the replayed prefix, or the hovered row, bounds how much has "happened"
    const revealed = state.iteratePath.count > 0 ? Math.min(state.iteratePath.count, ellipsoids.count) : ellipsoids.count;
    const active = Math.min(state.highlightIteratePathIndex ?? revealed - 1, ellipsoids.count - 1);
    if (active < 0) {
      this.hideFrom(0);
      return;
    }

    sampleIndices(active, this.indices);
    const is3D = snap.mode === "3d";
    let used = 0;
    for (let j = 0; j < this.indices.length; j++) {
      const index = this.indices[j]!;
      const segments = this.slots[used]!;
      if (!writeEllipseMatrix(this.matrix, ellipsoids, index, iterateHeightOrFloor(state.iteratePath, index))) {
        continue;
      }
      segments.matrix.copy(this.matrix);
      segments.matrixWorldNeedsUpdate = true;
      // slot styling ramps with recency, not with the slot's own index, so a
      // short run still ends on the bold "current" ellipse
      segments.material = slotMaterial(TRAIL_COUNT - this.indices.length + j, is3D);
      segments.visible = true;
      used++;
    }
    this.hideFrom(used);

    // The localizing polyhedron is shown only for a hovered (or replayed) iterate: one per trail
    // slot buries the picture, so it reads as an inspection tool instead.
    this.showLocalizingSet(state, is3D);
  }

  private showLocalizingSet(state: State, is3D: boolean): void {
    const index = state.highlightIteratePathIndex;
    const written = index === null ? 0 : this.writePolygon(state.iterateLocalizingSets, index, iterateHeightOrFloor(state.iteratePath, index));
    if (written === 0) this.polygon.hide();
    else this.polygon.draw(this.polygonScratch.subarray(0, written), is3D);
  }

  // The localizing polygon as segment endpoint pairs, closed back to the start. A 3-variable
  // localizing set arrives as half-spaces (stride 4) and is not drawn yet.
  private writePolygon(sets: LocalizingSetPath | null, index: number, z: number): number {
    if (!sets || sets.stride !== localizingSetStride(2) || index >= sets.count) return 0;
    const start = sets.offsets[index]!;
    const end = sets.offsets[index + 1]!;
    const count = end - start;
    if (count < 2) return 0;

    const needed = count * 6;
    if (this.polygonScratch.length < needed) {
      this.polygonScratch = new Float32Array(needed);
    }
    for (let i = 0; i < count; i++) {
      const from = (start + i) * 2;
      const to = (start + ((i + 1) % count)) * 2;
      const base = i * 6;
      this.polygonScratch[base] = sets.points[from]!;
      this.polygonScratch[base + 1] = sets.points[from + 1]!;
      this.polygonScratch[base + 2] = z;
      this.polygonScratch[base + 3] = sets.points[to]!;
      this.polygonScratch[base + 4] = sets.points[to + 1]!;
      this.polygonScratch[base + 5] = z;
    }
    return needed;
  }

  private hideFrom(slot: number): void {
    for (let i = slot; i < this.slots.length; i++) {
      this.slots[i]!.visible = false;
    }
  }

  override dispose(): void {
    super.dispose();
    this.geometry.dispose();
  }
}

// Opacity/width ramp by age. The values come from a fixed slot index so the
// shared line-material cache sees a small, stable set of keys.
function slotOpacity(slot: number) {
  const t = TRAIL_COUNT > 1 ? slot / (TRAIL_COUNT - 1) : 1;
  return OLDEST_OPACITY + (ACTIVE_OPACITY - OLDEST_OPACITY) * t;
}

function slotMaterial(slot: number, is3D: boolean) {
  const newest = slot >= TRAIL_COUNT - 1;
  return lineDepthMaterial(PALETTE.ellipsoid, newest ? STROKE.ellipsoidActive : STROKE.ellipsoidTrail, is3D, Number(slotOpacity(slot).toFixed(3)));
}
